import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import {
  CheckCircleIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  GlobeAltIcon,
  CogIcon,
  StarIcon,
  ArrowRightIcon,
  PlayIcon,
  CheckIcon,
  XMarkIcon,
  UserGroupIcon,
  BoltIcon,
  DocumentTextIcon,
  CalendarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid,
  StarIcon as StarIconSolid,
} from '@heroicons/react/24/solid';

const LandingPage: React.FC = () => {
  const { isDark } = useTheme();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const features = [
    {
      icon: ClockIcon,
      title: 'Real-Time Monitoring',
      description: 'Monitor your websites 24/7 with instant alerts and comprehensive uptime tracking.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: ChartBarIcon,
      title: 'Performance Analytics',
      description: 'Get detailed Lighthouse audits and actionable optimization recommendations.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Security Scanning',
      description: 'Advanced security monitoring with malware detection and vulnerability alerts.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: GlobeAltIcon,
      title: 'SEO Optimization',
      description: 'Comprehensive SEO analysis with broken link detection and ranking insights.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: CogIcon,
      title: 'Automated Reports',
      description: 'Smart reporting system with customizable schedules and detailed insights.',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: CheckCircleIcon,
      title: 'Multi-Site Dashboard',
      description: 'Manage unlimited websites from a single, powerful, intuitive dashboard.',
      color: 'from-teal-500 to-cyan-500'
    }
  ];

  const howItWorksSteps = [
    {
      step: '01',
      title: 'Connect Your Sites',
      description: 'Add your websites with a simple URL. Our system automatically detects your site configuration.',
      icon: GlobeAltIcon,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      step: '02',
      title: 'Configure Monitoring',
      description: 'Set up monitoring intervals, alerts, and choose which metrics matter most to your business.',
      icon: CogIcon,
      color: 'from-purple-500 to-pink-500'
    },
    {
      step: '03',
      title: 'Get Instant Alerts',
      description: 'Receive real-time notifications via email, SMS, or Slack when issues are detected.',
      icon: BoltIcon,
      color: 'from-orange-500 to-red-500'
    },
    {
      step: '04',
      title: 'Analyze & Optimize',
      description: 'Use detailed reports and recommendations to improve your site performance and SEO.',
      icon: ChartBarIcon,
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const integrations = [
    { name: 'Slack', logo: '💬', description: 'Real-time alerts' },
    { name: 'Discord', logo: '🎮', description: 'Team notifications' },
    { name: 'Telegram', logo: '📱', description: 'Mobile alerts' },
    { name: 'Webhook', logo: '🔗', description: 'Custom integrations' },
    { name: 'Email', logo: '📧', description: 'Email notifications' },
    { name: 'SMS', logo: '📲', description: 'Text alerts' },
    { name: 'PagerDuty', logo: '🚨', description: 'Incident management' },
    { name: 'Zapier', logo: '⚡', description: 'Workflow automation' }
  ];

  const blogPosts = [
    {
      title: 'Website Monitoring Best Practices for 2024',
      excerpt: 'Learn the essential strategies for effective website monitoring and how to prevent costly downtime.',
      author: 'Sarah Chen',
      date: 'Dec 15, 2024',
      readTime: '5 min read',
      category: 'Best Practices',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop'
    },
    {
      title: 'How to Improve Your Website Performance Score',
      excerpt: 'Discover proven techniques to boost your Lighthouse performance scores and enhance user experience.',
      author: 'Marcus Rodriguez',
      date: 'Dec 12, 2024',
      readTime: '7 min read',
      category: 'Performance',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop'
    },
    {
      title: 'Security Monitoring: Protecting Your Digital Assets',
      excerpt: 'Essential security monitoring practices to protect your websites from modern cyber threats.',
      author: 'Emily Watson',
      date: 'Dec 10, 2024',
      readTime: '6 min read',
      category: 'Security',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=250&fit=crop'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'CTO at TechFlow',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face',
      content: 'SaaS Monitor has revolutionized how we handle website monitoring. The instant alerts saved us from a major outage last month.',
      rating: 5
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Lead Developer at StartupXYZ',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face',
      content: 'The performance insights are incredible. We improved our site speed by 40% using their optimization recommendations.',
      rating: 5
    },
    {
      name: 'Emily Watson',
      role: 'Product Manager at Scale Inc',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face',
      content: 'Best monitoring tool we\'ve used. The dashboard is intuitive and the automated reports save us hours every week.',
      rating: 5
    }
  ];

  const plans = [
    {
      name: 'Starter',
      price: '$0',
      period: '/month',
      description: 'Perfect for personal projects',
      features: [
        '1 Website monitoring',
        'Basic uptime checks',
        'Email notifications',
        'Weekly reports',
        'Community support'
      ],
      limitations: ['Limited to 1 site', 'Basic features only'],
      cta: 'Start Free',
      popular: false,
      color: 'from-gray-500 to-gray-600'
    },
    {
      name: 'Professional',
      price: '$29',
      period: '/month',
      description: 'Ideal for growing businesses',
      features: [
        '20 Website monitoring',
        'Advanced performance analytics',
        'SMS + Email alerts',
        'Daily detailed reports',
        'API access',
        'SEO monitoring',
        'Security scans',
        'Priority support'
      ],
      limitations: [],
      cta: 'Start 14-day trial',
      popular: true,
      color: 'from-blue-500 to-purple-600'
    },
    {
      name: 'Enterprise',
      price: '$99',
      period: '/month',
      description: 'For large-scale operations',
      features: [
        'Unlimited websites',
        'White-label reports',
        'Custom integrations',
        'Dedicated account manager',
        '24/7 phone support',
        'Advanced security features',
        'Custom SLA',
        'On-premise deployment'
      ],
      limitations: [],
      cta: 'Contact Sales',
      popular: false,
      color: 'from-purple-600 to-pink-600'
    }
  ];

  const stats = [
    { number: 50000, label: 'Websites Monitored', suffix: '+' },
    { number: 99.9, label: 'Uptime Guarantee', suffix: '%' },
    { number: 24, label: 'Hours Monitoring', suffix: '/7' },
    { number: 1000, label: 'Happy Customers', suffix: '+' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 overflow-hidden ${
      isDark ? 'bg-gray-900' : 'bg-white'
    }`}>
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        <div className={`absolute inset-0 transition-colors duration-300 ${
          isDark 
            ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
            : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
        }`}></div>
        <div className={`absolute top-0 -left-4 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob ${
          isDark ? 'bg-blue-400' : 'bg-purple-300'
        }`}></div>
        <div className={`absolute top-0 -right-4 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000 ${
          isDark ? 'bg-purple-400' : 'bg-yellow-300'
        }`}></div>
        <div className={`absolute -bottom-8 left-20 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000 ${
          isDark ? 'bg-pink-400' : 'bg-pink-300'
        }`}></div>
      </div>

      {/* Header */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className={`relative z-50 backdrop-blur-lg shadow-sm border-b transition-colors duration-300 ${
          isDark 
            ? 'bg-gray-900/80 border-gray-700' 
            : 'bg-white/80 border-gray-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 20 }}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <h1 className={`text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${
                isDark 
                  ? 'from-white to-gray-300' 
                  : 'from-gray-900 to-gray-600'
              }`}>
                SaaS Monitor
              </h1>
            </motion.div>
            <motion.div 
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex items-center space-x-4"
            >
              <ThemeToggle />
              <Link
                to="/login"
                className={`font-medium transition-colors duration-200 ${
                  isDark 
                    ? 'text-gray-300 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Get Started
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section with Animated Background */}
      <section className="relative z-10 pt-20 pb-32 overflow-hidden">
        {/* Additional Animated Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-500 rounded-full animate-ping opacity-40"></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-purple-500 rounded-full animate-pulse opacity-60"></div>
          <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-pink-500 rounded-full animate-bounce opacity-30"></div>
          <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-cyan-500 rounded-full animate-ping opacity-50"></div>
          
          {/* Floating Shapes */}
          <motion.div
            animate={{
              y: [0, -20, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-1/4 right-1/4 w-8 h-8 border-2 border-blue-400 rounded-lg opacity-20"
          />
          <motion.div
            animate={{
              y: [0, 30, 0],
              x: [0, -15, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute bottom-1/3 right-1/3 w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-30"
          />
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, -180, -360],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-1/2 left-1/4 w-4 h-4 border border-orange-400 rotate-45 opacity-25"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-left"
            >
              <motion.h1 
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="text-5xl lg:text-7xl font-bold leading-tight mb-6"
              >
                <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                  isDark 
                    ? 'from-white via-blue-200 to-purple-200' 
                    : 'from-gray-900 via-blue-900 to-purple-900'
                }`}>
                  Monitor Your
                </span>
                <br />
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Websites
                </span>
                <br />
                <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                  isDark 
                    ? 'from-white via-blue-200 to-purple-200' 
                    : 'from-gray-900 via-blue-900 to-purple-900'
                }`}>
                  Like a Pro
                </span>
              </motion.h1>
              
              <motion.p 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className={`text-xl mb-8 leading-relaxed ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                The most advanced website monitoring platform with real-time alerts, 
                performance optimization, and comprehensive analytics. Keep your sites 
                running perfectly 24/7.
              </motion.p>
              
              <motion.div 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 mb-8"
              >
                <Link
                  to="/register"
                  className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                >
                  Start Free Trial
                  <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button
                  onClick={() => setIsVideoPlaying(true)}
                  className={`group border-2 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center ${
                    isDark 
                      ? 'border-gray-600 text-gray-300 hover:border-blue-500 hover:text-blue-400' 
                      : 'border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                  }`}
                >
                  <PlayIcon className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
                  Watch Demo
                </button>
              </motion.div>

              <motion.div 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className={`flex items-center space-x-6 text-sm ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <div className="flex items-center">
                  <CheckCircleIconSolid className="w-5 h-5 text-green-500 mr-2" />
                  Free 14-day trial
                </div>
                <div className="flex items-center">
                  <CheckCircleIconSolid className="w-5 h-5 text-green-500 mr-2" />
                  No credit card required
                </div>
                <div className="flex items-center">
                  <CheckCircleIconSolid className="w-5 h-5 text-green-500 mr-2" />
                  Cancel anytime
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="relative"
            >
              <div className="relative z-10">
                <motion.div
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [0, 1, 0]
                  }}
                  transition={{ 
                    duration: 6,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  className={`rounded-2xl shadow-2xl p-8 border transition-colors duration-300 ${
                    isDark 
                      ? 'bg-gray-800 border-gray-700' 
                      : 'bg-white border-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className={`font-semibold ${
                      isDark ? 'text-gray-100' : 'text-gray-900'
                    }`}>Live Dashboard</h3>
                    <div className="flex space-x-1">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <motion.div 
                      animate={{ width: ["0%", "100%"] }}
                      transition={{ delay: 1, duration: 2 }}
                      className={`rounded-lg p-4 ${
                        isDark ? 'bg-gray-700' : 'bg-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-sm font-medium ${
                          isDark ? 'text-gray-300' : 'text-gray-600'
                        }`}>Website Status</span>
                        <span className="text-green-600 font-bold">Online</span>
                      </div>
                      <div className={`w-full rounded-full h-2 ${
                        isDark ? 'bg-gray-600' : 'bg-gray-200'
                      }`}>
                        <motion.div 
                          animate={{ width: ["0%", "98%"] }}
                          transition={{ delay: 1.5, duration: 1.5 }}
                          className="bg-green-500 h-2 rounded-full"
                        ></motion.div>
                      </div>
                    </motion.div>
                    
                    <motion.div 
                      animate={{ opacity: [0, 1] }}
                      transition={{ delay: 2.5 }}
                      className="grid grid-cols-2 gap-4"
                    >
                      <div className={`rounded-lg p-3 text-center ${
                        isDark ? 'bg-blue-900/30' : 'bg-blue-50'
                      }`}>
                        <div className="text-2xl font-bold text-blue-600">
                          <CountUp end={99.9} decimals={1} duration={2} />%
                        </div>
                        <div className={`text-xs ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>Uptime</div>
                      </div>
                      <div className={`rounded-lg p-3 text-center ${
                        isDark ? 'bg-purple-900/30' : 'bg-purple-50'
                      }`}>
                        <div className="text-2xl font-bold text-purple-600">
                          <CountUp end={345} duration={2} />ms
                        </div>
                        <div className={`text-xs ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>Response</div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
              
              {/* Background decorations */}
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <motion.section 
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={controls}
        className="relative z-10 py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center text-white"
              >
                <div className="text-4xl lg:text-5xl font-bold mb-2">
                  <CountUp 
                    end={stat.number} 
                    duration={2.5}
                    decimals={stat.number % 1 !== 0 ? 1 : 0}
                  />
                  {stat.suffix}
                </div>
                <div className="text-blue-100 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* How It Works Section */}
      <section className={`relative z-10 py-24 transition-colors duration-300 ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                isDark 
                  ? 'from-white to-gray-300' 
                  : 'from-gray-900 to-gray-600'
              }`}>
                How It
              </span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {" "}Works
              </span>
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Get started with professional website monitoring in just 4 simple steps
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center relative"
              >
                <div className="relative mb-8">
                  <div className={`w-20 h-20 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    isDark ? 'bg-gray-700' : 'bg-gray-900'
                  }`}>
                    {step.step}
                  </div>
                </div>
                
                <h3 className={`text-xl font-bold mb-4 ${
                  isDark ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {step.title}
                </h3>
                
                <p className={`leading-relaxed ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {step.description}
                </p>

                {/* Connecting Line */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 opacity-30"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`relative z-10 py-24 transition-colors duration-300 ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                isDark 
                  ? 'from-white to-gray-300' 
                  : 'from-gray-900 to-gray-600'
              }`}>
                Powerful Features for
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Modern Monitoring
              </span>
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Everything you need to keep your websites running smoothly, all in one comprehensive platform
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className={`group relative rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700 hover:border-gray-600' 
                    : 'bg-white border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className={`text-xl font-bold mb-4 transition-colors ${
                    isDark 
                      ? 'text-gray-100 group-hover:text-gray-200' 
                      : 'text-gray-900 group-hover:text-gray-700'
                  }`}>
                    {feature.title}
                  </h3>
                  
                  <p className={`leading-relaxed ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Partners Section */}
      <section className={`relative z-10 py-24 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-gray-50 to-blue-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                isDark 
                  ? 'from-white to-gray-300' 
                  : 'from-gray-900 to-gray-600'
              }`}>
                Seamless
              </span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {" "}Integrations
              </span>
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Connect with your favorite tools and get alerts where you need them most
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
            {integrations.map((integration, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.2 }
                }}
                className={`text-center p-6 rounded-xl transition-all duration-300 ${
                  isDark 
                    ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600' 
                    : 'bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl border border-gray-100'
                }`}
              >
                <div className="text-3xl mb-3">{integration.logo}</div>
                <h3 className={`font-semibold mb-2 ${
                  isDark ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {integration.name}
                </h3>
                <p className={`text-sm ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {integration.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className={`relative z-10 py-24 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-gray-900 to-blue-900' 
          : 'bg-gradient-to-br from-gray-50 to-blue-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                isDark 
                  ? 'from-white to-gray-300' 
                  : 'from-gray-900 to-gray-600'
              }`}>
                Loved by
              </span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {" "}Developers
              </span>
            </h2>
            <p className={`text-xl ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              See what our customers are saying about SaaS Monitor
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3 }
                }}
                className={`rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${
                  isDark ? 'bg-gray-800' : 'bg-white'
                }`}
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIconSolid key={i} className="w-5 h-5 text-yellow-400" />
                  ))}
                </div>
                
                <p className={`mb-6 italic leading-relaxed ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  "{testimonial.content}"
                </p>
                
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <h4 className={`font-semibold ${
                      isDark ? 'text-gray-100' : 'text-gray-900'
                    }`}>{testimonial.name}</h4>
                    <p className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={`relative z-10 py-24 transition-colors duration-300 ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                isDark 
                  ? 'from-white to-gray-300' 
                  : 'from-gray-900 to-gray-600'
              }`}>
                Simple, Transparent
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Pricing
              </span>
            </h2>
            <p className={`text-xl ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Choose the perfect plan for your monitoring needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className={`relative rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 ${
                  plan.popular 
                    ? 'border-blue-500 scale-105' 
                    : isDark ? 'border-gray-700' : 'border-gray-100'
                } ${isDark ? 'bg-gray-800' : 'bg-white'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className={`text-2xl font-bold mb-2 ${
                    isDark ? 'text-gray-100' : 'text-gray-900'
                  }`}>{plan.name}</h3>
                  <p className={`mb-4 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>{plan.description}</p>
                  <div className="mb-6">
                    <span className={`text-5xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${
                      isDark 
                        ? 'from-white to-gray-300' 
                        : 'from-gray-900 to-gray-600'
                    }`}>
                      {plan.price}
                    </span>
                    <span className={`text-lg ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>{plan.period}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{feature}</span>
                    </div>
                  ))}
                  {plan.limitations.map((limitation, limitationIndex) => (
                    <div key={limitationIndex} className="flex items-center opacity-50">
                      <XMarkIcon className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" />
                      <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>{limitation}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/register"
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-center transition-all duration-300 flex items-center justify-center ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transform hover:scale-105'
                      : isDark 
                        ? 'border-2 border-gray-600 text-gray-300 hover:border-blue-500 hover:text-blue-400'
                        : 'border-2 border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                  }`}
                >
                  {plan.cta}
                  <ArrowRightIcon className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className={`relative z-10 py-24 transition-colors duration-300 ${
        isDark ? 'bg-gray-800' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                isDark 
                  ? 'from-white to-gray-300' 
                  : 'from-gray-900 to-gray-600'
              }`}>
                Latest from our
              </span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {" "}Blog
              </span>
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Stay updated with the latest insights, tips, and best practices in website monitoring
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3 }
                }}
                className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 ${
                  isDark ? 'bg-gray-900' : 'bg-white'
                }`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className={`flex items-center text-sm mb-3 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {post.date}
                    <span className="mx-2">•</span>
                    <EyeIcon className="w-4 h-4 mr-2" />
                    {post.readTime}
                  </div>
                  
                  <h3 className={`text-xl font-bold mb-3 leading-tight ${
                    isDark ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    {post.title}
                  </h3>
                  
                  <p className={`leading-relaxed mb-4 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                        <span className="text-white text-sm font-bold">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <span className={`text-sm font-medium ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {post.author}
                      </span>
                    </div>
                    
                    <button className={`text-sm font-medium transition-colors ${
                      isDark 
                        ? 'text-blue-400 hover:text-blue-300' 
                        : 'text-blue-600 hover:text-blue-700'
                    }`}>
                      Read More →
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button className={`border-2 px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:border-blue-500 hover:text-blue-400' 
                : 'border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600'
            }`}>
              View All Posts
            </button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Monitor Like a Pro?
            </h2>
            <p className="text-xl text-blue-100 mb-10 leading-relaxed">
              Join thousands of developers and businesses who trust SaaS Monitor 
              to keep their websites running perfectly. Start your free trial today!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
              >
                Start Your Free Trial
                <ArrowRightIcon className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/login"
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Sign In
              </Link>
            </div>
            
            <p className="text-blue-100 text-sm mt-6">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`relative z-10 py-16 transition-colors duration-300 ${
        isDark ? 'bg-gray-900 text-white' : 'bg-gray-900 text-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <ChartBarIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">SaaS Monitor</h3>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                The most advanced website monitoring platform for modern businesses. 
                Keep your sites running perfectly with real-time monitoring and alerts.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">tw</span>
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">li</span>
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">gh</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 SaaS Monitor. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Terms of Service</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {isVideoPlaying && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setIsVideoPlaying(false)}
        >
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.5 }}
            className="relative max-w-4xl w-full aspect-video bg-gray-900 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsVideoPlaying(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-white text-center">
                <PlayIcon className="w-20 h-20 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Demo video would be embedded here</p>
                <p className="text-sm text-gray-400 mt-2">Click the X to close</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;